import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";
import { Gift, Copy, Check, Users, Coins, ArrowRight } from "@phosphor-icons/react";

const ReferralPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [applyCode, setApplyCode] = useState("");
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/referral/stats`);
      setStats(res.data);
    } catch (e) { console.warn("Failed to fetch referral stats:", e); } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleCopy = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleApply = async () => {
    setApplying(true);
    setApplyResult(null);
    try {
      const res = await axios.post(`${API}/referral/apply`, { code: applyCode });
      setApplyResult({ success: true, message: res.data.message });
      setApplyCode("");
    } catch (err) {
      setApplyResult({ success: false, message: err.response?.data?.detail || "Failed to apply code" });
    } finally {
      setApplying(false);
    }
  };

  const shareUrl = stats?.code ? `${window.location.origin}/login?ref=${stats.code}` : "";

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="w-8 h-8 border-3 border-[#E91E8C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="referral-page">
      {/* Hero */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 sm:p-8">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-400/20 flex items-center justify-center flex-shrink-0">
            <Gift size={28} weight="duotone" className="text-amber-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-zinc-900 mb-1">Refer a Friend, Earn 10 Credits</h2>
            <p className="text-zinc-600 text-sm leading-relaxed">
              Share your unique referral code with fellow creators. When they sign up and enter your code, you both earn 10 free credits.
            </p>
          </div>
        </div>
      </div>

      {/* Your Code */}
      <div className="bg-white border border-zinc-200 rounded-2xl p-6">
        <h3 className="font-semibold text-zinc-900 mb-4">Your Referral Code</h3>
        {stats?.code ? (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl font-mono text-lg text-zinc-900 font-bold tracking-wider" data-testid="referral-code">
                {stats.code}
              </div>
              <button
                onClick={() => handleCopy(stats.code)}
                data-testid="copy-referral-code"
                className={`px-4 py-3 rounded-xl font-semibold text-sm transition-all flex items-center gap-2 ${copied ? "bg-green-500 text-white" : "bg-[#E91E8C] text-white hover:bg-[#d4177d]"}`}
              >
                {copied ? <><Check size={16} /> Copied!</> : <><Copy size={16} /> Copy</>}
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleCopy(shareUrl)}
                className="text-sm text-[#E91E8C] hover:underline font-medium"
              >
                Copy share link
              </button>
            </div>
          </>
        ) : (
          <p className="text-zinc-500">Loading your referral code...</p>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-zinc-200 rounded-xl p-4 text-center">
          <Users size={22} className="text-[#E91E8C] mx-auto mb-2" />
          <p className="text-2xl font-bold text-zinc-900">{stats?.uses || 0}</p>
          <p className="text-xs text-zinc-500">Referrals</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-xl p-4 text-center">
          <Coins size={22} className="text-amber-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-zinc-900">{stats?.total_earned || 0}</p>
          <p className="text-xs text-zinc-500">Credits Earned</p>
        </div>
        <div className="bg-white border border-zinc-200 rounded-xl p-4 text-center">
          <Gift size={22} className="text-emerald-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-zinc-900">10</p>
          <p className="text-xs text-zinc-500">Credits per Referral</p>
        </div>
      </div>

      {/* Apply Code */}
      <div className="bg-white border border-zinc-200 rounded-2xl p-6">
        <h3 className="font-semibold text-zinc-900 mb-3">Have a referral code?</h3>
        <div className="flex gap-3">
          <input
            type="text"
            value={applyCode}
            onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
            placeholder="Enter code (e.g. TBB-A1B2C3D4)"
            data-testid="apply-referral-input"
            className="flex-1 px-4 py-2.5 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E91E8C]/30 font-mono"
          />
          <button
            onClick={handleApply}
            disabled={!applyCode || applying}
            data-testid="apply-referral-btn"
            className="px-5 py-2.5 bg-zinc-900 text-white font-semibold text-sm rounded-xl hover:bg-zinc-800 disabled:opacity-40 transition-all"
          >
            {applying ? "Applying..." : "Apply"}
          </button>
        </div>
        {applyResult && (
          <p className={`mt-3 text-sm font-medium ${applyResult.success ? "text-emerald-600" : "text-red-500"}`} data-testid="apply-result">
            {applyResult.message}
          </p>
        )}
      </div>

      {/* Recent Referrals */}
      {stats?.referrals?.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-2xl p-6">
          <h3 className="font-semibold text-zinc-900 mb-4">Recent Referrals</h3>
          <div className="space-y-3">
            {stats.referrals.map((ref) => (
              <div key={ref.id} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                <div>
                  <p className="text-sm font-medium text-zinc-900">{ref.referee_name || "New User"}</p>
                  <p className="text-xs text-zinc-500">{new Date(ref.created_at).toLocaleDateString()}</p>
                </div>
                <span className="text-sm font-semibold text-emerald-600">+{ref.credits_earned} credits</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralPage;
